import * as React from 'react';
import { Document, Page } from 'react-pdf';

import { Button } from '@acceligentllc/storybook';

import styles from './styles.module.scss';

type PdfQueueTask = {
	canceled: boolean;
	start: () => void;
	promise: Promise<void>;
};

let pdfQueueTail: Promise<void> = Promise.resolve();

function enqueuePdfLoad(task: PdfQueueTask): void {
	pdfQueueTail = pdfQueueTail.then(() => {
		if (task.canceled) {
			return undefined;
		}
		task.start();
		return task.promise;
	}).catch(() => undefined);
}

interface OwnProps {
	file: string;
}

const PDF_PREFETCH_MARGIN = '0px 0px 600px 0px';

const PdfView: React.FC<OwnProps> = ({ file }) => {
	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const loadResolveRef = React.useRef<Nullable<() => void>>(null);

	const [inViewport, setInViewport] = React.useState<boolean>(false);
	const [attempt, setAttempt] = React.useState<number>(0);
	const [isActive, setIsActive] = React.useState<boolean>(false);
	const [numPages, setNumPages] = React.useState<number>(0);

	React.useEffect(() => {
		const element = wrapperRef.current;
		if (!element || !file) {
			return undefined;
		}
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setInViewport(true);
				}
			});
		}, { rootMargin: PDF_PREFETCH_MARGIN });
		observer.observe(element);
		return () => {
			observer.disconnect();
		};
	}, [file]);

	React.useEffect(() => {
		if (!file || !inViewport) {
			return undefined;
		}
		const loadPromise = new Promise<void>((resolve) => {
			loadResolveRef.current = resolve;
		});
		const task: PdfQueueTask = {
			canceled: false,
			start: () => setIsActive(true),
			promise: loadPromise,
		};
		enqueuePdfLoad(task);
		return () => {
			task.canceled = true;
			const resolve = loadResolveRef.current;
			if (resolve) {
				resolve();
			}
		};
	}, [file, inViewport, attempt, loadResolveRef]);

	const handleDocumentLoadSuccess = React.useCallback(({ numPages: pageTotal }: { numPages: number; }) => {
		setNumPages(pageTotal);
		loadResolveRef.current?.();
	}, []);

	const handleDocumentLoadError = React.useCallback(() => {
		loadResolveRef.current?.();
	}, []);

	const handleRetry = React.useCallback(() => {
		setNumPages(0);
		setIsActive(false);
		setAttempt((previous) => previous + 1);
	}, []);

	if (!file) {
		return null;
	}

	return (
		<div className={styles.pdfView} ref={wrapperRef}>
			{!isActive ? (
				<div className={styles.pdfView__state}>
					<span className={styles.pdfView__spinner} />
					<span>Loading document...</span>
				</div>
			) : (
				<Document
					error={
						<div className={styles.pdfView__state}>
							<span>Failed to load document.</span>
							<Button label="Try again" onClick={handleRetry} style="secondary" type="button" />
						</div>
					}
					file={file}
					key={attempt}
					loading={
						<div className={styles.pdfView__state}>
							<span className={styles.pdfView__spinner} />
							<span>Loading document...</span>
						</div>
					}
					onLoadError={handleDocumentLoadError}
					onLoadSuccess={handleDocumentLoadSuccess}
				>
					{Array.from({ length: numPages }, (_unused, index) => (
						<Page key={index + 1} pageNumber={index + 1} />
					))}
				</Document>
			)}
		</div>
	);
};

export default React.memo(PdfView);
